import { Injectable } from '@angular/core';
import {
  AuthSession,
  ConfirmResetPasswordInput,
  ConfirmSignInInput,
  ConfirmUserAttributeInput,
  DeleteUserAttributesInput,
  FetchUserAttributesOutput,
  ResetPasswordInput,
  SendUserAttributeVerificationCodeInput,
  SignInInput,
  UpdateMFAPreferenceInput,
  UpdatePasswordInput,
  UpdateUserAttributesInput,
  confirmResetPassword,
  confirmSignIn,
  confirmUserAttribute,
  deleteUserAttributes,
  fetchAuthSession,
  fetchMFAPreference,
  fetchUserAttributes,
  resetPassword,
  sendUserAttributeVerificationCode,
  signIn,
  signOut,
  updateMFAPreference,
  updatePassword,
  updateUserAttributes,
} from 'aws-amplify/auth';
import { Observable, defer } from 'rxjs';
import { ignoreElements, map } from 'rxjs/operators';

import { AuthSignInStep } from '../models/auth.model';
import { AuthGateway } from '../usecases/auth.gateway';

@Injectable()
export class AuthService extends AuthGateway {
  fetchAuthSession(forceRefresh = false): Observable<AuthSession> {
    return defer(async () => await fetchAuthSession({ forceRefresh }));
  }

  fetchMFAPreference(): Observable<'SMS' | 'TOTP' | 'EMAIL' | undefined> {
    return defer(async () => (await fetchMFAPreference()).preferred);
  }

  fetchUserAttributes(): Observable<FetchUserAttributesOutput> {
    return defer(async () => await fetchUserAttributes());
  }

  signIn(params: SignInInput): Observable<AuthSignInStep> {
    return defer(async () => await signIn(params)).pipe(map(output => output.nextStep.signInStep));
  }

  confirmSignIn(params: ConfirmSignInInput): Observable<AuthSignInStep> {
    return defer(async () => await confirmSignIn(params)).pipe(map(output => output.nextStep.signInStep));
  }

  signOut(): Observable<void> {
    return defer(async () => await signOut());
  }

  resetPassword(params: ResetPasswordInput): Observable<AuthSignInStep> {
    return defer(async () => await resetPassword(params)).pipe(map(output => output.nextStep.resetPasswordStep as AuthSignInStep));
  }

  confirmResetPassword(params: ConfirmResetPasswordInput): Observable<void> {
    return defer(async () => await confirmResetPassword(params));
  }

  updatePassword(params: UpdatePasswordInput): Observable<void> {
    return defer(async () => await updatePassword(params));
  }

  updateUserAttribute(params: UpdateUserAttributesInput): Observable<never> {
    return defer(async () => await updateUserAttributes(params)).pipe(ignoreElements());
  }

  verifyUserAttribute(params: SendUserAttributeVerificationCodeInput): Observable<never> {
    return defer(async () => await sendUserAttributeVerificationCode(params)).pipe(ignoreElements());
  }

  verifyUserAttributeSubmit(params: ConfirmUserAttributeInput): Observable<never> {
    return defer(async () => await confirmUserAttribute(params)).pipe(ignoreElements());
  }

  deleteUserAttribute(params: DeleteUserAttributesInput): Observable<never> {
    return defer(async () => await deleteUserAttributes(params)).pipe(ignoreElements());
  }

  setPreferredMFA(params: UpdateMFAPreferenceInput): Observable<never> {
    return defer(async () => await updateMFAPreference(params)).pipe(ignoreElements());
  }
}
